import * as React from 'react';

/**
 * Diz se o usuário parou de mexer na tela há mais de `espera` milissegundos.
 *
 * Existe para telas que se beneficiam de recarregar sozinhas — uma fila de
 * trabalho que muda porque OUTRAS pessoas estão trabalhando — sem cometer o
 * erro que o MegaFaturamento comete: ele se recarrega a cada 90 s de
 * ociosidade e leva junto tudo que foi digitado nos campos de volume. Quem
 * estava no meio de conferir dez pedidos perde os dez.
 *
 * A diferença é o que conta como "mexer". Aqui conta qualquer sinal de
 * presença — teclado, ponteiro, rolagem, toque —, e o relógio reinicia a cada
 * um deles. Enquanto a pessoa estiver ali, o valor é `false` e a tela não se
 * mexe sozinha.
 *
 * A ABA ESCONDIDA NÃO CONTA COMO OCIOSA
 * -------------------------------------
 * Uma aba em segundo plano não recebe evento nenhum, e sem essa checagem ela
 * ficaria eternamente "ociosa", recarregando de minuto em minuto para ninguém
 * ver — gastando banco e, nesta tela, chamadas ao ERP. Quando a aba volta a
 * ficar visível, a contagem recomeça do zero: quem acabou de voltar para a
 * tela está prestes a mexer nela.
 */
export function useOcioso(espera = 30_000): boolean {
  const [ocioso, definirOcioso] = React.useState(false);

  React.useEffect(() => {
    let relogio: ReturnType<typeof setTimeout>;

    const reiniciar = (): void => {
      definirOcioso(false);
      clearTimeout(relogio);
      // Aba escondida não arma o relógio: ninguém está olhando.
      if (document.visibilityState === 'visible') {
        relogio = setTimeout(() => definirOcioso(true), espera);
      }
    };

    /*
     * `passive` em todos: são ouvintes de presença, nunca cancelam o evento.
     * Sem isso, o listener de `touchstart`/`scroll` entra no caminho crítico
     * da rolagem e trava o dedo numa lista longa.
     */
    const eventos = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;
    for (const evento of eventos) {
      window.addEventListener(evento, reiniciar, { passive: true });
    }
    document.addEventListener('visibilitychange', reiniciar);

    reiniciar();

    return () => {
      clearTimeout(relogio);
      for (const evento of eventos) window.removeEventListener(evento, reiniciar);
      document.removeEventListener('visibilitychange', reiniciar);
    };
  }, [espera]);

  return ocioso;
}
